import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  BadgePercent,
  BookOpen,
  CheckCircle2,
  ClipboardList,
  Globe2,
  LogIn,
  MapPin,
  MessageSquareText,
  PackagePlus,
  PackageSearch,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  Star,
  Tags,
  Truck,
  WalletCards,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Badge, Card, ErrorState, Loading, PageHeader } from "../../components/ui";
import { adminApi, assetUrl } from "../../lib/api";
import { dateTime, money, number } from "../../lib/format";
import { labelOf } from "../../lib/labels";
import type { AdminProductListItem, OrderListItem } from "../../types/api";

type DashboardStatProps = {
  label: string;
  value: string;
  note: string;
  to: string;
  icon: LucideIcon;
  tone?: "default" | "gold" | "blue" | "rose";
};

function DashboardStat({ label, value, note, to, icon: Icon, tone = "default" }: DashboardStatProps) {
  return (
    <Link to={to} className={`stat-card stat-card--${tone}`}>
      <div className="stat-card__icon"><Icon /></div>
      <div className="stat-card__content">
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{note}</small>
      </div>
      <ArrowLeft className="stat-card__arrow" />
    </Link>
  );
}

function DashboardQuickAction({ to, label, icon: Icon }: { to: string; label: string; icon: LucideIcon }) {
  return (
    <Link className="dashboard-quick-action" to={to}>
      <Icon size={18} />
      <span>{label}</span>
      <ArrowLeft size={15} />
    </Link>
  );
}

function RecentOrderRow({ order }: { order: OrderListItem }) {
  return (
    <Link to={`/orders/${order.id}`} className="compact-list__row">
      <div className="compact-list__avatar"><ClipboardList /></div>
      <div className="compact-list__content">
        <strong>سفارش {order.orderNumber}</strong>
        <small>{dateTime(order.createdAt)} · {number(order.itemCount)} قلم</small>
      </div>
      <div className="compact-list__end">
        <Badge value={labelOf(order.status)} />
        <strong>{money(order.grandTotal)}</strong>
      </div>
    </Link>
  );
}

function RecentProductRow({ row }: { row: AdminProductListItem }) {
  return (
    <Link to={`/products/${row.product.id}/details`} className="compact-list__row">
      <div className="product-thumb">
        {row.product.image ? <img src={assetUrl(row.product.image)} alt={row.product.name} /> : <PackageSearch />}
      </div>
      <div className="compact-list__content">
        <strong>{row.product.name}</strong>
        <small>{row.product.brand} · موجودی {number(row.product.stock)}</small>
      </div>
      <div className="compact-list__end">
        <Badge value={labelOf(row.status)} />
        <strong>{money(row.product.price)}</strong>
      </div>
    </Link>
  );
}

function AttentionItem({
  to,
  value,
  label,
  description,
  icon: Icon,
  tone,
}: {
  to: string;
  value: string;
  label: string;
  description: string;
  icon: LucideIcon;
  tone: "amber" | "sky" | "emerald";
}) {
  return (
    <Link to={to} className={`attention-item attention-item--${tone}`}>
      <span className="attention-item__icon"><Icon /></span>
      <div>
        <strong>{value}</strong>
        <span>{label}</span>
        <small>{description}</small>
      </div>
      <ArrowLeft />
    </Link>
  );
}

function FinancialMetric({ label, value, icon: Icon, tone = "default" }: { label: string; value: string; icon: LucideIcon; tone?: "default" | "gold" | "rose" | "aqua" }) {
  return (
    <div className={`finance-metric finance-metric--${tone}`}>
      <span className="finance-metric__icon"><Icon /></span>
      <div><span>{label}</span><strong>{value}</strong></div>
    </div>
  );
}

function browserLabel(userAgent?: string | null): string {
  if (!userAgent) return "اطلاعات مرورگر ثبت نشده";
  if (/Edg\//i.test(userAgent)) return "Microsoft Edge";
  if (/Firefox\//i.test(userAgent)) return "Mozilla Firefox";
  if (/Chrome\//i.test(userAgent) && !/Edg\//i.test(userAgent)) return "Google Chrome";
  if (/Safari\//i.test(userAgent) && !/Chrome\//i.test(userAgent)) return "Safari";
  if (/OPR\//i.test(userAgent)) return "Opera";
  return "مرورگر ناشناخته";
}

export function DashboardPage() {
  const products = useQuery({
    queryKey: ["dashboard", "products"],
    queryFn: () => adminApi.products.list({ page: 1, pageSize: 5, sort: "created-desc" }),
  });
  const orders = useQuery({
    queryKey: ["dashboard", "orders"],
    queryFn: () => adminApi.orders.list({ page: 1, pageSize: 5, sort: "created-desc" }),
  });
  const brands = useQuery({ queryKey: ["brands"], queryFn: adminApi.brands.list });
  const reviews = useQuery({
    queryKey: ["dashboard", "reviews"],
    queryFn: () => adminApi.reviews.list({ status: "Pending", page: 1, pageSize: 1 }),
  });
  const contacts = useQuery({
    queryKey: ["dashboard", "contacts"],
    queryFn: () => adminApi.contacts.list({ status: "New", page: 1, pageSize: 1 }),
  });
  const summary = useQuery({
    queryKey: ["dashboard", "summary"],
    queryFn: adminApi.dashboard.summary,
  });

  if (products.isLoading || orders.isLoading || brands.isLoading) return <Loading />;

  const error = products.error || orders.error || brands.error;
  if (error) {
    return (
      <ErrorState
        error={error}
        retry={() => {
          void products.refetch();
          void orders.refetch();
          void brands.refetch();
          void summary.refetch();
        }}
      />
    );
  }

  const pendingReviews = reviews.data?.totalCount ?? 0;
  const newContacts = contacts.data?.totalCount ?? 0;
  const activeBrands = brands.data?.filter((item) => item.isActive).length ?? 0;
  const attentionCount = pendingReviews + newContacts;
  const sales = summary.data?.sales;
  const lastLogin = summary.data?.lastLogin;

  return (
    <>
      <PageHeader
        title="داشبورد مدیریت"
        description="نمای مدیریتی فروش، سفارش‌ها، کاتالوگ و موارد نیازمند اقدام؛ اطلاعات مهم بدون شلوغی در دسترس است."
        actions={
          <>
            <Link className="button button--ghost" to="/orders"><ClipboardList size={17} /> سفارش‌ها</Link>
            <Link className="button button--primary" to="/products/new"><PackagePlus size={18} /> محصول جدید</Link>
          </>
        }
      />

      <section className="dashboard-command">
        <div className="dashboard-command__main">
          <div className="dashboard-command__eyebrow"><Sparkles size={17} /> مرکز عملیات عطر لیا</div>
          <h2>مدیریت روزانه، سریع‌تر و متمرکزتر</h2>
          <p>اول موارد نیازمند اقدام را ببینید؛ سپس سفارش‌ها و محتوای فروشگاه را از میان‌برهای همین بخش مدیریت کنید.</p>
          <div className="dashboard-command__actions">
            <DashboardQuickAction to="/products/new" label="افزودن محصول" icon={PackagePlus} />
            <DashboardQuickAction to="/blog/new" label="مقاله جدید" icon={BookOpen} />
            <DashboardQuickAction to="/shipping" label="مدیریت ارسال" icon={Truck} />
          </div>
        </div>
        <div className="dashboard-command__focus">
          <div className="dashboard-command__focus-head">
            <span><ShieldCheck /> وضعیت امروز</span>
            <span className={attentionCount ? "status-dot is-warning" : "status-dot is-success"} />
          </div>
          <strong>{number(attentionCount)}</strong>
          <p>{attentionCount ? "مورد هنوز به بررسی مدیر نیاز دارد." : "صف اقدامات فوری خالی است."}</p>
          <div className={attentionCount ? "dashboard-command__state is-warning" : "dashboard-command__state is-success"}>
            <CheckCircle2 size={16} />
            {attentionCount ? "نیازمند پیگیری" : "همه‌چیز مرتب است"}
          </div>
        </div>
      </section>

      <div className="stats-grid">
        <DashboardStat label="محصولات فروشگاه" value={number(products.data?.totalCount ?? 0)} note="کاتالوگ و موجودی" to="/products" icon={PackageSearch} />
        <DashboardStat label="سفارش‌های ثبت‌شده" value={number(orders.data?.totalCount ?? 0)} note="پرداخت تا تحویل" to="/orders" icon={ClipboardList} tone="gold" />
        <DashboardStat label="برندهای فعال" value={number(activeBrands)} note="برندهای قابل نمایش" to="/brands" icon={Tags} tone="blue" />
        <DashboardStat label="نیازمند رسیدگی" value={number(attentionCount)} note="دیدگاه و پیام جدید" to={pendingReviews > 0 ? "/reviews" : "/contacts"} icon={Star} tone="rose" />
      </div>

      <div className="dashboard-bento dashboard-bento--expanded">
        <div className="dashboard-bento__main-column">
          <Card
            title="آخرین سفارش‌ها"
            description="آخرین سفارش‌های ثبت‌شده و وضعیت فعلی آن‌ها"
            className="dashboard-bento__orders"
            action={<Link className="text-link" to="/orders">مشاهده همه <ArrowLeft size={16} /></Link>}
          >
            <div className="compact-list">
              {orders.data?.items.length
                ? orders.data.items.map((order) => <RecentOrderRow order={order} key={order.id} />)
                : <div className="dashboard-empty-inline">هنوز سفارشی ثبت نشده است.</div>}
            </div>
          </Card>

          <Card
            title="محصولات تازه ثبت‌شده"
            description="آخرین تغییرات کاتالوگ فروشگاه"
            className="dashboard-bento__products"
            action={<Link className="text-link" to="/products">مشاهده کاتالوگ <ArrowLeft size={16} /></Link>}
          >
            <div className="compact-list">
              {products.data?.items.length
                ? products.data.items.map((row) => <RecentProductRow row={row} key={row.product.id} />)
                : <div className="dashboard-empty-inline">هنوز محصولی در کاتالوگ ثبت نشده است.</div>}
            </div>
          </Card>
        </div>

        <div className="dashboard-bento__side-column">
          <Card title="خلاصه مالی فروش" description="مجموع ارقام سفارش‌های پرداخت‌شده" className="dashboard-bento__finance">
            {summary.isLoading ? (
              <div className="dashboard-inline-loading">در حال محاسبه اطلاعات مالی…</div>
            ) : summary.error ? (
              <div className="dashboard-inline-error">
                <span>اطلاعات مالی در دسترس نیست.</span>
                <button type="button" onClick={() => void summary.refetch()}>تلاش دوباره</button>
              </div>
            ) : (
              <div className="finance-grid">
                <FinancialMetric label="درآمد نهایی" value={money(sales?.revenue ?? 0)} icon={WalletCards} tone="aqua" />
                <FinancialMetric label="جمع مبلغ کالا" value={money(sales?.grossSubtotal ?? 0)} icon={ReceiptText} />
                <FinancialMetric label="تخفیف اعمال‌شده" value={money(sales?.discountAmount ?? 0)} icon={BadgePercent} tone="rose" />
                <FinancialMetric label="هزینه حمل" value={money(sales?.shippingAmount ?? 0)} icon={Truck} tone="gold" />
              </div>
            )}
            <div className="finance-footer">
              <span>سفارش پرداخت‌شده <b>{number(sales?.paidOrders ?? 0)}</b></span>
              <span>سفارش در جریان <b>{number(sales?.pendingOrders ?? 0)}</b></span>
            </div>
          </Card>

          <Card title="مرکز پیگیری" description="مواردی که بهتر است امروز بررسی شوند" className="dashboard-bento__attention">
            <div className="attention-stack">
              <AttentionItem to="/reviews" value={number(pendingReviews)} label="دیدگاه در انتظار" description="بررسی و تعیین وضعیت دیدگاه مشتریان" icon={Star} tone="amber" />
              <AttentionItem to="/contacts" value={number(newContacts)} label="پیام تماس جدید" description="پاسخ‌گویی و ثبت نتیجه پیگیری" icon={MessageSquareText} tone="sky" />
              <AttentionItem to="/shipping" value="ارسال" label="تنظیمات حمل‌ونقل" description="تعرفه، آستانه رایگان و زمان تحویل" icon={Truck} tone="emerald" />
            </div>
          </Card>

          <Card title="آخرین ورود مدیر" description="اطلاعات امنیتی آخرین ورود موفق ثبت‌شده" className="dashboard-bento__login">
            <div className="login-audit-list">
              <div><span><LogIn /></span><div><small>زمان ورود</small><strong>{dateTime(lastLogin?.lastLoginAt)}</strong></div></div>
              <div><span><Globe2 /></span><div><small>مرورگر</small><strong>{browserLabel(lastLogin?.userAgent)}</strong></div></div>
              <div><span><MapPin /></span><div><small>آدرس IP</small><strong dir="ltr">{lastLogin?.ipAddress || "—"}</strong></div></div>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
